import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CardButton } from "../../core/components/buttons/CardButton";
import { InfoText } from "../../core/components/texts/InfoText";
import {
  getChainConfigLoaded,
  getChainPractices,
} from "../../core/store/chainSlice";
import { useMainStore } from "../../core/store/mainStore";
import { useHandleParamNavigation } from "../../core/utils/useHandleParamNavigation";
import { AppointmentScreenWrapper } from "../onboarding/AppointmentScreenWrapper";
import {
  resolveStepAfterPracticeSelection,
  usePracticeSelectionSkip,
} from "./utils/appointmentFlow";

export const AppointmentPracticeSelectionScreen = () => {
  const { t } = useTranslation();
  const navigate = useHandleParamNavigation();
  const setPracticeId = useMainStore((state) => state.setCurrentPracticeId);
  const chainConfigLoaded = useMainStore(getChainConfigLoaded);
  const practices = useMainStore(getChainPractices);
  const { isSkipped, practiceId, bookablePractices } =
    usePracticeSelectionSkip();

  // PF-5480: safety net next to the entry resolver in AppointmentLayout — this screen is also reachable
  // later (sidebar jump, getTargetScreen defaults) and must not show up as a selection then.
  useEffect(() => {
    if (!chainConfigLoaded) return;
    if (bookablePractices.length === 0) {
      navigate("/appointment/appointmentUnavailable", { replace: true });
    } else if (isSkipped && practiceId) {
      setPracticeId(practiceId);
      const targetStep = resolveStepAfterPracticeSelection();
      navigate(`/appointment/${targetStep}`, { replace: true });
    }
  }, [
    chainConfigLoaded,
    bookablePractices,
    isSkipped,
    practiceId,
    navigate,
    setPracticeId,
  ]);

  if (!chainConfigLoaded || isSkipped || bookablePractices.length === 0) {
    return null;
  }

  const handleSelect = (selectedPracticeId: string) => {
    setPracticeId(selectedPracticeId);
    const targetStep = resolveStepAfterPracticeSelection();
    navigate(`/appointment/${targetStep}`);
  };

  return (
    <AppointmentScreenWrapper hideNextButton hideBackButton>
      <InfoText
        headline={t("APPOINTMENT_PRACTICE_SELECTION_HEADLINE")}
        text={[t("APPOINTMENT_PRACTICE_SELECTION_DESCRIPTION")]}
      />
      <CardsContainer>
        {bookablePractices.map((practice) => (
          <CardButton
            key={practice.practiceId}
            onClick={() => handleSelect(practice.practiceId)}
            title={
              practice.appointmentRequestFlags?.practiceTitle ||
              practice.practiceName ||
              practices.find((p) => p.practiceId === practice.practiceId)
                ?.practiceName
            }
            subtitle={practice.appointmentRequestFlags?.practiceDescription}
            imageUrl={practice.appointmentRequestFlags?.practiceImageUrl}
          />
        ))}
      </CardsContainer>
    </AppointmentScreenWrapper>
  );
};

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
